import { FC, MouseEvent, useEffect, useRef, useState } from 'react';
import { Document, Page } from 'react-pdf';
import ClipLoader from 'react-spinners/ClipLoader';
import { ZoomInOutlined, ZoomOutOutlined } from '@ant-design/icons';

type Props = {
  file?: Blob;
  loading: boolean;
  isFullPreview: boolean;
  onClick?: (e: MouseEvent<HTMLDivElement>) => void;
  pageWidth: number;
  onImageGenerated?: (
    imageUrl: string,
    optimalScale?: number,
    canvasDimensions?: { width: number; height: number },
  ) => void;
};

const FilePreview: FC<Props> = ({
  file,
  loading,
  isFullPreview,
  pageWidth = 2000,
  onClick,
  onImageGenerated,
}) => {
  const ref = useRef<HTMLDivElement | null>(null);
  const hasCalculatedScaleRef = useRef(false); // Track whether we've calculated scale already
  const isLoadingFile = !file || loading;
  const [width, setWidth] = useState(pageWidth);
  const [canvasRef, setCanvasRef] = useState<HTMLCanvasElement | null>(null);
  const [magnifierState, setMagnifierState] = useState({
    top: 0,
    left: 0,
    offsetX: 0,
    offsetY: 0,
  });
  const [isVisible, setIsVisible] = useState(false);
  const magnifierRadius = 150;
  const zoomFactor = 3; // Zoom factor for the magnifier
  const [scale, setScale] = useState(1);

  // Zoom in/out functions
  const zoomIn = () => setScale((prev) => Math.min(prev + 0.2, 3)); // Max zoom 3x
  const zoomOut = () => setScale((prev) => Math.max(prev - 0.2, 0.4)); // Min zoom 0.4x

  // Reset state when file changes
  useEffect(() => {
    hasCalculatedScaleRef.current = false; // Reset flag when file changes
  }, [file]);

  // Calculate optimal scale to fit PDF in container
  const calculateOptimalScale = (
    canvasWidth: number,
    canvasHeight: number,
    containerWidth: number,
    containerHeight: number,
  ) => {
    if (!containerWidth || !containerHeight || !canvasWidth || !canvasHeight) {
      return 1;
    }

    const scaleX = containerWidth / canvasWidth;
    const scaleY = containerHeight / canvasHeight;

    console.log('Scale X:', scaleX, 'Scale Y:', scaleY);

    // Use the smaller scale to ensure PDF fits entirely
    const optimalScale = (1 / Math.max(scaleX, scaleY)) * 0.8; // 80% to add margin

    return Math.max(0.3, Math.min(optimalScale, 3));
  };

  // Handle container resizing
  useEffect(() => {
    const handleResize = () => {
      if (!ref.current) return;
      const containerWidth = ref.current.clientWidth;
      const adjustedWidth = containerWidth * 0.95; // 95% of container width
      setWidth(adjustedWidth);
    };

    // Initial size calculation
    handleResize();

    // Observe container size changes
    const resizeObserver = new ResizeObserver(handleResize);
    if (ref.current) {
      resizeObserver.observe(ref.current);
    }

    // Cleanup
    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  const handleRenderSuccess = () => {
    try {
      const canvasElements = document.getElementsByTagName('canvas');
      const canvas = canvasElements[canvasElements.length - 1];

      if (!canvas) {
        throw new Error('No canvas element found in the document');
      }

      if (canvas.width === 0 || canvas.height === 0) {
        throw new Error('Canvas dimensions are zero, cannot generate image');
      }

      setCanvasRef(canvas);

      // Generate canvas image for parent component
      const imageUrl = canvas.toDataURL('image/png');

      console.log('Canvas size:', canvas.width, 'x', canvas.height);

      // Instead of trying to calculate the scale here, just pass the canvas dimensions
      // to the parent component which has better context about the container size
      if (onImageGenerated) {
        // Pass image URL and canvas dimensions to parent for scale calculation
        const canv = { width: canvas.width, height: canvas.height };
        onImageGenerated(imageUrl, undefined, canv);
      }
    } catch (error) {
      console.error('Error capturing canvas:', error);
    }
  };

  // Empty event handlers
  const handleMouseMove = () => {};
  const handleMouseLeave = () => {};

  return (
    <div
      style={{
        width: '100%',
        height: '100%',
        position: 'relative',
        padding: '20px',
      }}
      ref={ref}
      onClick={onClick}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
    >
      {isLoadingFile ? (
        <ClipLoader color="#35b3ee" />
      ) : (
        <Document file={file} loading={<ClipLoader color="#35b3ee" />}>
          <Page pageNumber={1} width={width} scale={scale} onRenderSuccess={handleRenderSuccess} />
        </Document>
      )}
    </div>
  );
};

export default FilePreview;
